import { FormEvent, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  Archive,
  ArrowDownToLine,
  Boxes,
  CircleDollarSign,
  ClipboardList,
  PackageCheck,
  PackageMinus,
  PackagePlus,
  RefreshCcw,
  ShieldCheck,
  Truck,
  Warehouse,
  X,
} from "lucide-react";
import { getInventoryOverview, inventoryAction } from "../api";
import type { AuthUser, InventoryOverview, InventoryStock, WorkOrderPartDemand } from "../types";

type Filter = "all" | "low" | "critical";
type Action = "receive" | "reserve" | "issue" | "release";

const money = (value: number) =>
  new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    maximumFractionDigits: 0,
  }).format(value);

export function PartsInventoryPanel({
  open,
  onClose,
  user,
}: {
  open: boolean;
  onClose: () => void;
  user: AuthUser;
}) {
  const [overview, setOverview] = useState<InventoryOverview | null>(null);
  const [selectedStockId, setSelectedStockId] = useState<number | null>(null);
  const [filter, setFilter] = useState<Filter>("all");
  const [action, setAction] = useState<Action>("receive");
  const [demandId, setDemandId] = useState<number | null>(null);
  const [quantity, setQuantity] = useState("1");
  const [unitCost, setUnitCost] = useState("");
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");

  function refresh() {
    getInventoryOverview()
      .then((row) => {
        setOverview(row);
        setSelectedStockId((current) =>
          row.stocks.some((stock) => stock.stock_id === current)
            ? current
            : row.stocks[0]?.stock_id ?? null,
        );
        setError("");
      })
      .catch((cause) =>
        setError(cause instanceof Error ? cause.message : "No fue posible cargar el almacén"),
      );
  }

  useEffect(() => {
    if (!open) return;
    refresh();
    const onUpdate = () => refresh();
    window.addEventListener("gsf:inventory-updated", onUpdate);
    return () => window.removeEventListener("gsf:inventory-updated", onUpdate);
  }, [open]);

  const visibleStocks = useMemo(
    () => (overview?.stocks || []).filter((stock) => filter === "all" || stock.status === filter),
    [filter, overview],
  );
  const selected =
    visibleStocks.find((stock) => stock.stock_id === selectedStockId) ||
    visibleStocks[0] ||
    overview?.stocks[0];
  const demands = (overview?.work_order_demands || []).filter(
    (demand) => demand.part_id === selected?.part_id,
  );
  const canManage =
    user.permissions?.includes("maintenance.manage") ??
    ["superadmin", "fleet_admin", "maintenance"].includes(user.role);
  const summary = useMemo(
    () => ({
      value: (overview?.stocks || []).reduce((sum, stock) => sum + stock.inventory_value, 0),
      low: (overview?.stocks || []).filter((stock) => stock.status === "low").length,
      critical: (overview?.stocks || []).filter((stock) => stock.status === "critical").length,
      requisitions: (overview?.requisitions || []).filter((row) => ["open", "ordered"].includes(row.status)).length,
    }),
    [overview],
  );

  function prepareDemand(demand: WorkOrderPartDemand) {
    setDemandId(demand.maintenance_part_usage_id);
    setQuantity(String(demand.quantity));
    setAction(demand.status === "reserved" ? "issue" : "reserve");
    setReference(demand.work_order_number);
    setNote(
      demand.status === "reserved"
        ? `Entrega a técnico para ${demand.work_order_number}.`
        : `Reserva para ${demand.work_order_number}.`,
    );
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!selected) return;
    setPending(true);
    setError("");
    try {
      const updated = await inventoryAction({
        action,
        part_id: selected.part_id,
        location_id: selected.location_id,
        quantity: Number(quantity),
        unit_cost: Number(unitCost || 0),
        maintenance_part_usage_id: action === "receive" ? undefined : demandId ?? undefined,
        reference,
        note,
      });
      setOverview(updated);
      setDemandId(null);
      setQuantity("1");
      setUnitCost("");
      setReference("");
      setNote("");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "No fue posible registrar el movimiento");
    } finally {
      setPending(false);
    }
  }

  if (!open) return null;
  return (
    <div className="admin-overlay" role="presentation" onMouseDown={onClose}>
      <aside className="admin-panel inventory-panel" role="dialog" aria-modal="true" aria-labelledby="inventory-title" onMouseDown={(event) => event.stopPropagation()}>
        <header>
          <div>
            <span className="eyebrow">MANTENIMIENTO · ALMACÉN Y ABASTECIMIENTO</span>
            <h2 id="inventory-title">Refacciones</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Cerrar refacciones"><X size={18} /></button>
        </header>
        <section className="admin-panel__summary">
          <span><CircleDollarSign size={17} /><b>{money(summary.value)}</b> inventario</span>
          <span><AlertTriangle size={17} /><b>{summary.critical}</b> agotadas</span>
          <span><Boxes size={17} /><b>{summary.low}</b> bajo mínimo</span>
          <span><ClipboardList size={17} /><b>{summary.requisitions}</b> requisiciones</span>
        </section>
        <div className="inventory-body">
          <section className="inventory-catalog">
            <div className="inventory-filters">
              {(["all", "low", "critical"] as Filter[]).map((item) => (
                <button type="button" className={filter === item ? "is-active" : ""} onClick={() => setFilter(item)} key={item}>
                  {{ all: "TODAS", low: "BAJO MÍNIMO", critical: "AGOTADAS" }[item]}
                </button>
              ))}
              <button type="button" onClick={refresh} aria-label="Actualizar inventario"><RefreshCcw size={12} /></button>
            </div>
            <div className="inventory-list">
              {visibleStocks.map((stock) => (
                <button type="button" className={`inventory-item inventory-item--${stock.status}${selected?.stock_id === stock.stock_id ? " is-selected" : ""}`} onClick={() => setSelectedStockId(stock.stock_id)} key={stock.stock_id}>
                  <span>{stock.status === "ok" ? <PackageCheck size={16} /> : <AlertTriangle size={16} />}</span>
                  <span><small>{stock.part_number} · {stock.location_code}</small><strong>{stock.description}</strong><em>Existencia {stock.on_hand} · reservado {stock.reserved}</em></span>
                  <b>{stock.available}<small>disponible</small></b>
                  <i><em style={{ width: `${Math.min(100, stock.available / Math.max(1, stock.minimum_stock * 2) * 100)}%` }} /></i>
                </button>
              ))}
              {!visibleStocks.length && <p className="maintenance-empty">SIN EXISTENCIAS EN ESTA VISTA</p>}
            </div>
          </section>
          <section className="inventory-detail">
            {selected && (
              <>
                <div className={`inventory-status inventory-status--${selected.status}`}>
                  <Warehouse size={34} />
                  <span><small>{selected.location_name}</small><b>{selected.status === "ok" ? "DISPONIBLE" : selected.status === "low" ? "BAJO MÍNIMO" : "AGOTADA"}</b></span>
                </div>
                <div className="inventory-title">
                  <span className="eyebrow">{selected.part_number} · {selected.category}</span>
                  <h3>{selected.description}</h3>
                  <p>{selected.location_name} · actualizado {new Date(selected.updated_at).toLocaleString("es-MX")}</p>
                </div>
                <div className="inventory-kpis">
                  <article><Archive size={15} /><span><small>FÍSICO</small><strong>{selected.on_hand} {selected.unit_label}</strong></span></article>
                  <article><ShieldCheck size={15} /><span><small>RESERVADO</small><strong>{selected.reserved} {selected.unit_label}</strong></span></article>
                  <article><PackageCheck size={15} /><span><small>DISPONIBLE</small><strong>{selected.available} {selected.unit_label}</strong></span></article>
                  <article><CircleDollarSign size={15} /><span><small>VALOR</small><strong>{money(selected.inventory_value)}</strong></span></article>
                </div>
                <div className="inventory-demands">
                  <span className="eyebrow">DEMANDA DE ÓRDENES</span>
                  {demands.map((demand) => (
                    <button type="button" className={`is-${demand.status}`} onClick={() => prepareDemand(demand)} key={demand.maintenance_part_usage_id}>
                      <Truck size={14} />
                      <span><small>{demand.work_order_number} · {demand.vehicle_unit_code}</small><strong>{demand.quantity} × {demand.description}</strong></span>
                      <b>{demand.status}</b>
                    </button>
                  ))}
                  {!demands.length && <p className="maintenance-empty">SIN DEMANDA ACTIVA</p>}
                </div>
                {canManage && (
                  <form className="inventory-action" onSubmit={submit}>
                    <span className="eyebrow">MOVIMIENTO DE ALMACÉN</span>
                    <label>OPERACIÓN<select value={action} onChange={(event) => setAction(event.target.value as Action)}>
                      <option value="receive">Recibir compra</option>
                      <option value="reserve">Reservar para OT</option>
                      <option value="issue">Entregar a técnico</option>
                      <option value="release">Liberar reserva</option>
                    </select></label>
                    <label>CANTIDAD<input required type="number" min=".01" step=".01" value={quantity} onChange={(event) => setQuantity(event.target.value)} /></label>
                    <label>COSTO UNITARIO<input type="number" min="0" step=".01" disabled={action !== "receive"} value={unitCost} onChange={(event) => setUnitCost(event.target.value)} /></label>
                    <label>REFERENCIA<input value={reference} onChange={(event) => setReference(event.target.value)} placeholder="Factura, OC u orden de trabajo" /></label>
                    {action !== "receive" && <label className="inventory-action__wide">DEMANDA<select required value={demandId ?? ""} onChange={(event) => {
                      const demand = demands.find((item) => item.maintenance_part_usage_id === Number(event.target.value));
                      if (demand) prepareDemand(demand);
                    }}><option value="">Seleccionar orden...</option>{demands.map((demand) => <option value={demand.maintenance_part_usage_id} key={demand.maintenance_part_usage_id}>{demand.work_order_number} · {demand.status}</option>)}</select></label>}
                    <label className="inventory-action__wide">NOTA<input required value={note} onChange={(event) => setNote(event.target.value)} placeholder="Motivo y evidencia del movimiento" /></label>
                    {error && <p className="admin-form-message is-error">{error}</p>}
                    <button className="primary-button" disabled={pending}>
                      {action === "receive" ? <PackagePlus size={14} /> : action === "issue" ? <PackageMinus size={14} /> : <ArrowDownToLine size={14} />}
                      {pending ? "REGISTRANDO..." : "REGISTRAR MOVIMIENTO"}
                    </button>
                  </form>
                )}
                <div className="inventory-requisitions">
                  <span className="eyebrow">REQUISICIONES DE ESTA REFACCIÓN</span>
                  {overview?.requisitions.filter((row) => row.part_id === selected.part_id).map((row) => (
                    <article key={row.id}><ClipboardList size={14} /><span><small>{row.requisition_number} · {row.status}</small><strong>{row.quantity} {selected.unit_label} · {row.reason}</strong></span></article>
                  ))}
                </div>
                {!canManage && error && <p className="admin-form-message is-error">{error}</p>}
              </>
            )}
          </section>
        </div>
      </aside>
    </div>
  );
}
